import { AgenteClientService } from './../../../../client/agente-client/agente-client.service';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalConfirmComponent } from '@rsm/components/modal-confirm/modal-confirm.component';
import { AuthClientService } from '@rsm/client/auth-client';
import {
  CorporativeClientService,
  saveBlobAsPdf,
} from '@rsm/client/corporative-client';
import { MessageBuilder, MessageService } from '@rsm/shared/message';
import { SecurityService } from '@rsm/shared/security';
import { from } from 'rxjs';
import { PixPaymentMethod } from '@rsm/components/pix-payment-panel';
import { CompanySetupService } from '../company-setup.service';

@Component({
  selector: 'app-company-setup-checkout',
  templateUrl: './company-setup-checkout.component.html',
  styleUrls: ['./company-setup-checkout.component.scss'],
})
export class CompanySetupCheckoutComponent implements OnInit, OnDestroy {
  @ViewChild('modalConfirmHire') modalConfirmHire!: ModalConfirmComponent;

  planInfo = this.companySetupService.planInfo;
  currentUser: any;

  /** Valor total do plano (resolvido do serviço). */
  public price = 0;

  /** Saldo da carteira do agente. */
  public walletBalance: any;

  // ---------------------------------------------------------------------------
  // Computed — mesmo padrão do checkout de campanha
  // ---------------------------------------------------------------------------

  /** Valor que ainda falta pagar (max 0, preço − saldo + crédito adicional opcional). */
  get totalPrice(): number {
    return Math.max(0, this.price - (this.walletBalance?.valueAvailable || 0)) + (this.additionalCredit || 0);
  }

  /** Quanto do saldo será usado para cobrir o plano. */
  get walletDeduction(): number {
    return Math.min(this.walletBalance?.valueAvailable || 0, this.price);
  }

  /** Saldo que restará após a contratação (quando o saldo cobre tudo). */
  get walletAfterPlan(): number {
    return Math.max(0, (this.walletBalance?.valueAvailable || 0) - this.price);
  }

  /** True quando o saldo não é suficiente para cobrir o plano. */
  get isWalletInsufficient(): boolean {
    return (this.walletBalance?.valueAvailable || 0) < this.price;
  }

  /** Crédito adicional que o usuário deseja incluir além do plano (opcional). */
  public additionalCredit = 0;

  /** Controla a visibilidade do campo de crédito adicional. */
  public showAddCreditInput = false;

  /** Abre/fecha o campo de crédito adicional; limpa o valor ao fechar. */
  public toggleAddCreditInput(): void {
    this.showAddCreditInput = !this.showAddCreditInput;
    if (!this.showAddCreditInput) {
      this.additionalCredit = 0;
    }
  }

  // ---------------------------------------------------------------------------
  // Pagamento — widget visual padronizado (<rsm-pix-payment-display>)
  // ---------------------------------------------------------------------------

  public paymentMethod: PixPaymentMethod = 'pix';

  /** QR code retornado pelo hirePlan(). */
  public paymentReference = '';

  /** Id do movimento gerado pelo hirePlan() — usado para baixar o boleto. */
  public movementId = '';

  /** Vencimento da cobrança (billingDueDate do hirePlan) — exibido no boleto. */
  public dueDate: string | null = null;

  /** True enquanto o hirePlan() (geração do PIX) está em voo. */
  public isGeneratingPix = false;

  /** True enquanto o boleto (PDF) está sendo gerado/baixado. */
  public isGeneratingBoleto = false;

  /** True depois que o PIX foi gerado. */
  public pixGenerated = false;

  /** True enquanto o polling consulta o pagamento silenciosamente. */
  public checking = false;

  /** True quando o pagamento é confirmado e o plano é contratado. */
  public confirmed = false;

  /** True após 10 min de polling sem confirmação (o banco vira fallback). */
  public pollingTimedOut = false;

  /** True enquanto o plano está sendo contratado via saldo. */
  public confirmando = false;

  /** True enquanto o hirePlan() estiver em voo (evita duplo clique). */
  private isHiring = false;

  /** Id do PlainsCompany gerado pelo hirePlan() — usado no polling. */
  public planCompanyId = '';

  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingElapsedSecs = 0;

  // ---------------------------------------------------------------------------
  // Confirmação antes de qualquer ação financeira (modal-confirm)
  // ---------------------------------------------------------------------------

  /** Abre a confirmação antes de gerar o PIX. */
  public requestPix(): void {
    const planName = this.planInfo?.plan?.name || 'plano';
    const valor = `R$ ${this.totalPrice.toFixed(2).replace('.', ',')}`;
    this.modalConfirmHire.openModal(
      `Confirmar geração de PIX para contratação do plano "${planName}" no valor de ${valor}?`,
      'pix'
    );
  }

  /** Abre a confirmação antes de gerar a fatura (boleto). */
  public requestBoleto(): void {
    const planName = this.planInfo?.plan?.name || 'plano';
    const valor = `R$ ${this.totalPrice.toFixed(2).replace('.', ',')}`;
    this.modalConfirmHire.openModal(
      `Confirmar geração de fatura para contratação do plano "${planName}" no valor de ${valor}?`,
      'boleto'
    );
  }

  /** Abre a confirmação antes de contratar via saldo da carteira. */
  public requestHire(): void {
    const planName = this.planInfo?.plan?.name || 'plano';
    const valor = `R$ ${this.price.toFixed(2).replace('.', ',')}`;
    this.modalConfirmHire.openModal(
      `Confirmar contratação do plano "${planName}" por ${valor} usando saldo da carteira?`,
      'hire'
    );
  }

  /** Callback do modal — executa a ação confirmada. */
  public onConfirmAction(action: any): void {
    if (action === 'pix') this.generatePix();
    else if (action === 'boleto') this.generateBoleto();
    else if (action === 'hire') this.payWithBalance();
  }

  // ---------------------------------------------------------------------------
  // Métodos de pagamento
  // ---------------------------------------------------------------------------

  /** Seleciona o método de pagamento (PIX ou Fatura). */
  public selectPaymentMethod(method: PixPaymentMethod): void {
    if (method === 'card') return;
    this.paymentMethod = method;
  }

  public generatePix(): void {
    if (this.isHiring || this.isGeneratingPix) return;
    this.isHiring = true;
    this.isGeneratingPix = true;
    this.generatePlan(
      () => {
        this.isHiring = false;
        this.isGeneratingPix = false;
        if (!this.paymentReference && !this.movementId) {
          // Cobranca nao foi gerada — algo falhou silenciosamente no backend
          this.messageService.addMsgDanger({
            message: 'Não foi possível gerar a cobrança. Tente novamente ou entre em contato com o suporte.',
            autoclose: true,
          });
          return;
        }
        if (!this.paymentReference) {
          // Carteira cobriu tudo → sem QR code; polling confirmará e redirecionará
          this.messageService.addMsgSuccess({
            message: 'Pagamento realizado com saldo em carteira! Redirecionando...',
            autoclose: true,
          });
        }
      },
      () => {
        this.isHiring = false;
        this.isGeneratingPix = false;
        this.messageService.addMsgDanger({
          message: 'Erro ao gerar o PIX. Verifique sua conexão e tente novamente.',
          autoclose: true,
        });
      }
    );
  }

  /**
   * Gera o boleto: contrata o plano (cria o movimento, se ainda não houver)
   * e indica que o boleto está sendo gerado.
   */
  public generateBoleto(): void {
    if (this.isHiring || this.isGeneratingBoleto) return;
    if (this.pixGenerated && this.movementId && this.planCompanyId) return;

    this.isHiring = true;
    this.isGeneratingBoleto = true;
    this.generatePlan(
      () => {
        this.isHiring = false;
        this.isGeneratingBoleto = false;
      },
      () => {
        this.isHiring = false;
        this.isGeneratingBoleto = false;
        this.messageService.addMsgDanger({
          message: 'Erro ao gerar o boleto. Verifique sua conexão e tente novamente.',
          autoclose: true,
        });
      }
    );
  }

  /**
   * Baixa o boleto bolepix em PDF.
   * Só é chamado pelo rsm-boleto-view APÓS o movimento ter sido criado por
   * generateBoleto() — o movimento e o QR code já existem neste ponto.
   */
  public downloadBoleto(): void {
    if (this.isGeneratingBoleto) return;
    this.isGeneratingBoleto = true;
    this.fetchBoleto();
  }

  /** Gera o movimento/PIX do plano (hirePlan) e inicia o polling. */
  private generatePlan(onReady: () => void, onError: () => void): void {
    this.companySetupService.hirePlan().subscribe({
      next: (payment: any) => {
        this.paymentReference = payment?.qrCode || '';
        this.movementId = payment?.movementID || '';
        this.dueDate =
          payment?.billingDueDate || this.companySetupService.currentBillingDueDate || null;
        this.planCompanyId =
          payment?.planCompanyID || this.companySetupService.currentPlanCompanyId || '';

        // Só considera a cobrança gerada se houver QR code (PIX) ou movimento (boleto).
        // Saldo cobrindo tudo vem sem QR code, mas movementID é preenchido pelo corporativo.
        const billingGenerated = !!this.paymentReference || !!this.movementId;

        if (billingGenerated) {
          this.pixGenerated = true;
          this.startPolling();
        }

        onReady();
      },
      error: () => onError(),
    });
  }

  /** Baixa o PDF do boleto do plano (mesmo movimento/QR do PIX). */
  private fetchBoleto(): void {
    if (!this.movementId || !this.planCompanyId) {
      this.isGeneratingBoleto = false;
      this.messageService.addMsgDanger({
        message: 'Não foi possível gerar o boleto (dados do pagamento ausentes).',
        autoclose: true,
      });
      return;
    }

    this.corporativeService
      .downloadCompanyBoleto({
        movementID: this.movementId,
        planCompanyID: this.planCompanyId,
      })
      .subscribe({
        next: (blob) => {
          saveBlobAsPdf(blob, 'fatura-rsm-plano.pdf');
          this.isGeneratingBoleto = false;
        },
        error: () => {
          this.isGeneratingBoleto = false;
          this.messageService.addMsgDanger({
            message: 'Erro ao gerar a fatura. Tente novamente.',
            autoclose: true,
          });
        },
      });
  }

  /** Descarta o PIX/boleto atual e gera um novo (ex.: após timeout). */
  public retryPix(): void {
    this.stopPolling();
    this.pixGenerated = false;
    this.confirmed = false;
    this.pollingTimedOut = false;
    this.paymentReference = '';
    this.movementId = '';
    this.dueDate = null;
    this.planCompanyId = '';
    this.generatePix();
  }

  // ---------------------------------------------------------------------------
  // Contratação via saldo — quando o saldo cobre tudo (totalPrice === 0)
  // ---------------------------------------------------------------------------

  /**
   * Contrata o plano diretamente pelo saldo da carteira do agente.
   * Só deve ser chamado quando totalPrice === 0 (saldo suficiente).
   */
  public payWithBalance(): void {
    if (this.isHiring || this.confirmando) return;
    this.isHiring = true;
    this.confirmando = true;
    this.companySetupService.hirePlan().subscribe({
      next: (payment: any) => {
        this.isHiring = false;
        // Mesmo quando o saldo cobre tudo, precisamos do planCompanyId
        // para o redirect funcionar corretamente.
        this.planCompanyId =
          payment?.planCompanyID || this.companySetupService.currentPlanCompanyId || '';
        this.messageService.addMsgSuccess({
          message: 'Plano contratado com sucesso!',
          autoclose: true,
        });
        this.redirectToCompanyHome();
      },
      error: () => {
        this.isHiring = false;
        this.confirmando = false;
        this.messageService.addMsgDanger({
          message: 'Erro ao contratar o plano. Tente novamente.',
          autoclose: true,
        });
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Polling em camadas (5s nos primeiros 2 min, 15s até 10 min) — mesmo padrão
  // do crédito de carteira. Ao confirmar, o plano já foi ativado no backend e
  // seguimos para a home da empresa.
  // ---------------------------------------------------------------------------

  private startPolling(): void {
    if (!this.planCompanyId) return;
    this.pollingElapsedSecs = 0;
    this.checking = true;
    this.pollingTimedOut = false;
    // O overlay não é mais suprimido globalmente durante os até 10 min de
    // polling — isso deixava o app inteiro sem carregamento nesse período. A
    // própria consulta de status já roda silenciosa (`skipLoader`).
    this.scheduleNextPoll();
  }

  private scheduleNextPoll(): void {
    const intervalMs = this.pollingElapsedSecs < 120 ? 5_000 : 15_000;
    this.pollingTimer = setTimeout(() => this.pollOnce(), intervalMs);
  }

  private pollOnce(): void {
    this.companySetupService.checkPlanPayment(this.planCompanyId).subscribe({
      next: (status) => {
        if (status?.status === 'CONFIRMED') {
          this.stopPolling();
          this.confirmed = true;
          this.messageService.addMsgSuccess({
            message: 'Plano contratado com sucesso!',
            autoclose: true,
          });
          this.redirectToCompanyHome();
          return;
        }
        this.advanceAndReschedule();
      },
      error: () => this.advanceAndReschedule(),
    });
  }

  private advanceAndReschedule(): void {
    const stepSecs = this.pollingElapsedSecs < 120 ? 5 : 15;
    this.pollingElapsedSecs += stepSecs;
    if (this.pollingElapsedSecs >= 600) {
      this.stopPolling();
      this.pollingTimedOut = true;
      return;
    }
    this.scheduleNextPoll();
  }

  private stopPolling(): void {
    if (this.pollingTimer != null) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.checking = false;
  }

  // ---------------------------------------------------------------------------
  // Utilitários de exibição
  // ---------------------------------------------------------------------------

  copyCodeToClipboard() {
    if (!this.paymentReference) return;

    from(navigator.clipboard.writeText(this.paymentReference)).subscribe({
      next: () => {
        this.messageService.addMsgSuccess({
          message: 'Código PIX copiado com sucesso!',
          autoclose: true,
        });
      },
      error: (error) => {
        const message = MessageBuilder.newInstance(error)
          .addAnyField('message')
          .build();
        this.messageService.addMsgDanger(message);
      },
    });
  }

  getValueLicenses() {
    const unitPrice = Number(this.planInfo?.plan?.valueTaxaActive || 0);
    const licenseValue = this.getQtdUsers() * unitPrice;
    if (this.isDedicatedPlan()) {
      return Math.max(licenseValue, 1500);
    }
    return licenseValue;
  }

  getQtdUsers() {
    return this.planInfo.qtdLicenses;
  }

  getValueImposto() {
    return Number(this.planInfo.plan.valueTaxaActive) * 0.1333;
  }

  getUserName() {
    return this.currentUser?.name || '';
  }

  getLicenseSummaryLabel(): string {
    if (this.isDedicatedPlan()) {
      return `Licenças de usuário (${this.getQtdUsers()} por ${this.planInfo.validity || '1 ano'})`;
    }
    return `Licença de usuário X ${this.getQtdUsers()}`;
  }

  getRootUserLabel(): string {
    return this.planInfo.rootUserName || this.getUserName() || 'Agente cadastrante';
  }

  isDedicatedPlan(): boolean {
    return this.planInfo?.productKey === 'RSM_DEDICADA';
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  constructor(
    private companySetupService: CompanySetupService,
    private agentService: AgenteClientService,
    private authClientService: AuthClientService,
    private corporativeService: CorporativeClientService,
    private messageService: MessageService,
    private securityService: SecurityService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') as string);

    // Resolve o preço uma vez e armazena para os getters computados
    this.companySetupService.calculatePlanCost().subscribe((p) => {
      this.price = p || 0;
    });

    this.agentService.getWalletBalance(this.currentUser?.id).subscribe((res) => {
      this.walletBalance = res;
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // ---------------------------------------------------------------------------
  // Redirect pós-contratação
  // ---------------------------------------------------------------------------

  private redirectToCompanyHome(): void {
    this.authClientService.findAllContexts().subscribe({
      next: (contexts) => {
        const currentContext = (contexts || []).find(
          (item: any) => `${item.id}` === `${this.companySetupService.contextId}`
        );

        if (!currentContext?.userContextId) {
          this.router.navigate(['/access/context']);
          return;
        }

        this.authClientService
          .refresh({
            refreshToken: this.currentUser?.refreshToken,
            userContextId: currentContext.userContextId,
          })
          .subscribe({
            next: (data: any) => {
              this.securityService.init({
                id: data.userId,
                name: data.name,
                login: data.login,
                email: data.email,
                roles: data.roles,
                profile: data.profile,
                context: data.context,
                contextId: data.contextId,
                isRoot: data.isRoot,
                accessToken: data.accessToken,
                expiresIn: data.accessExpiresIn,
                refreshToken: data.refreshToken,
                userContextId: data.userContextId,
                expiredPassword: data.temporaryPassword,
              });
              this.router.navigate(['/home']);
            },
            error: () => this.router.navigate(['/access/context']),
          });
      },
      error: () => this.router.navigate(['/access/context']),
    });
  }
}
